import { HelpTip } from '@/pages/fulfillment/components/HelpTip';
import { DownloadIcon, HistoryIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { shelfLabel } from '../inventoryView';
import type { InventoryScreenRow } from '../inventoryView';
import { VERDICT_LABELS } from '../fullCountWalk';
import type { WalkHistoryEntry, WalkSummary } from '../fullCountWalk';

/** What the walk last did, read out under the band; the counter's eyes are on the shelf, not on a toast. */
export interface WalkStatus {
  text: string;
  kind: 'ok' | 'warn' | 'bad' | 'info';
}

/** What the band says about a full count walk and how it moves through it, behind its (?) [SSD-7662]. */
export const FULL_COUNT_HELP_LINES = [
  'A full count walk opens every shelf the filters keep, in shelf order (A1-2 before A1-10). Each Counted Qty starts blank: a blank box is not counted, 0 means nothing is on the shelf.',
  'Scan a shelf label, then each product on it: the product scanned goes to its own row on that shelf, you type what is there and Enter takes you back to the scan box for the next product.',
  'Previous and Next (F7 and F8) step to the shelf before or after. Rows left uncounted stay open; the band says how many before it moves on.',
  'One barcode can stand for more than one product, and a barcode in the catalog can be wrong, so when a code names several rows the walk asks which one you hold. Check the name and size on the box.',
  'A product with no row on the shelf is found below the band: by its barcode, or by name in the catalog search. Adding it opens a row at 0 for you to count; nothing new is created in the catalog.',
  'Not on shelf saves the row at 0 once you pick why. Recount puts the cursor back in the box: the new count is written against the stock as it stands then.',
  'Every count goes into the stock as soon as it is saved, as an ADJUST in Stock History. Export walk writes the whole walk and Export changes only the rows off the book, added or not on the shelf, in the columns of the FC stock-take file.',
];

export interface FullCountBandProps {
  shelves: string[];
  current: string | null;
  /** Rows of the walk on the current shelf, and how many of them nobody has counted yet. */
  shelfRows: number;
  shelfLeft: number;
  startedAt: string | null;
  barcodeless: number;
  summary: WalkSummary;
  status: WalkStatus | null;
  /** The rows the scanned code names on this shelf when it names more than one; the counter picks which. */
  choice: InventoryScreenRow[] | null;
  history: WalkHistoryEntry[];
  historyOpen: boolean;
  busy: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onPick: (row: InventoryScreenRow) => void;
  onToggleHistory: () => void;
  onExport: (changesOnly: boolean) => void;
}

const clock = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? '—'
    : at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
};

/**
 * The full count walk's band over the table: where the counter stands, how to step, what is left, and the walk's
 * numbers and exports [SSD-7662]. It holds no state of its own; the page owns the walk.
 */
export function FullCountBand({
  shelves,
  current,
  shelfRows,
  shelfLeft,
  startedAt,
  barcodeless,
  summary,
  status,
  choice,
  history,
  historyOpen,
  busy,
  onPrevious,
  onNext,
  onPick,
  onToggleHistory,
  onExport,
}: FullCountBandProps) {
  const at = current === null ? -1 : shelves.indexOf(current);
  const first = at <= 0;
  const last = at === shelves.length - 1;
  const where =
    current === null
      ? 'Scan a shelf label to begin'
      : shelfRows === 0
        ? 'Nothing is booked on this shelf. Scan what is there, or find it in the product search below.'
        : shelfLeft === 0
          ? `All ${shelfRows} ${shelfRows === 1 ? 'row' : 'rows'} counted`
          : `${shelfLeft} of ${shelfRows} ${shelfRows === 1 ? 'row' : 'rows'} left on this shelf`;
  return (
    <section className="fc-band" aria-label="Full count walk">
      <div className="fc-head">
        <button
          type="button"
          className="btn btn-line btn-sm fc-step"
          aria-label="Previous shelf (F7)"
          disabled={busy || first}
          onClick={onPrevious}
        >
          ‹ Previous <kbd>F7</kbd>
        </button>
        <div className="fc-shelf">
          <span className="fc-shelf-k">Shelf</span>
          <b className="fc-shelf-v" aria-live="polite">
            {current === null ? '—' : shelfLabel(current)}
          </b>
          <span className="fc-pos">{`${at >= 0 ? at + 1 : 0} / ${shelves.length}`}</span>
          {last && current !== null && <span className="status st-hold fc-last">Last shelf</span>}
        </div>
        <button
          type="button"
          className="btn btn-line btn-sm fc-step"
          aria-label="Next shelf (F8)"
          disabled={busy || last}
          onClick={onNext}
        >
          Next <kbd>F8</kbd> ›
        </button>
        <span className={shelfRows === 0 && current !== null ? 'fc-where fc-empty' : 'fc-where'}>{where}</span>
        <span className="sp" />
        <HelpTip topic="Full count walk" lines={FULL_COUNT_HELP_LINES} />
      </div>
      <div className="fc-facts">
        <span>{`${shelves.length} ${shelves.length === 1 ? 'shelf' : 'shelves'}`}</span>
        <span>{`${summary.rows} ${summary.rows === 1 ? 'row' : 'rows'}`}</span>
        <span>{`Started ${clock(startedAt)}`}</span>
        <span>{`${barcodeless} ${barcodeless === 1 ? 'product' : 'products'} without a barcode`}</span>
      </div>
      <div className="fc-sum">
        <span className="fc-num">
          <b>{summary.counted}</b> of {summary.rows} counted
        </span>
        <span className="fc-num">
          <b>{summary.matches}</b> match
        </span>
        <span className="fc-num">
          <b>{summary.changed}</b> off the book
        </span>
        <span className="fc-num">
          <b>{summary.added}</b> added
        </span>
        <span className="fc-num">
          <b>{summary.notOnShelf}</b> not on shelf
        </span>
        <span className="sp" />
        <button type="button" className="btn btn-gray btn-sm" onClick={() => onExport(false)}>
          <DownloadIcon />
          Export walk
        </button>
        <button type="button" className="btn btn-gray btn-sm" onClick={() => onExport(true)}>
          <DownloadIcon />
          Export changes
        </button>
        <button
          type="button"
          className="btn btn-line btn-sm"
          aria-expanded={historyOpen}
          aria-controls="fc-history"
          onClick={onToggleHistory}
        >
          <HistoryIcon />
          {`History (${history.length})`}
        </button>
      </div>
      <div role="status" aria-live="polite" className={status ? `fc-status fc-${status.kind}` : 'fc-status'}>
        {status?.text ?? ''}
      </div>
      {choice !== null && choice.length > 1 && (
        <div className="fc-choice" role="group" aria-label="Pick the product you hold">
          <span>{`This code names ${choice.length} products on this shelf. Check the box and pick one:`}</span>
          {choice.map((row) => (
            <button key={row.sku} type="button" className="btn btn-blue btn-sm" onClick={() => onPick(row)}>
              {[row.sku, row.brand, row.name, row.size].filter(Boolean).join(' · ')}
            </button>
          ))}
        </div>
      )}
      {historyOpen && (
        <div id="fc-history" className="fc-history">
          {history.length === 0 ? (
            <p className="blocked-note">Nothing is counted in this walk yet.</p>
          ) : (
            <table className="tbl fc-history-tbl" aria-label="Walk history">
              <colgroup>
                <col className="fc-h-time" />
                <col className="fc-h-shelf" />
                <col className="fc-h-sku" />
                <col className="fc-h-name" />
                <col className="fc-h-verdict" />
                <col className="fc-h-count" />
              </colgroup>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Shelf</th>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Verdict</th>
                  <th>Book → Counted</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={`${entry.sku}@${entry.locationCode}`}>
                    <td className="num">{clock(entry.at)}</td>
                    <td>{shelfLabel(entry.locationCode)}</td>
                    <td className="num">{entry.sku}</td>
                    <td>{entry.name ?? '—'}</td>
                    <td>{VERDICT_LABELS[entry.verdict]}</td>
                    <td className="num">{`${entry.bookAtCount} → ${entry.countedQty}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
